import { formatIsoToDisplay } from 'components/partials/date-picker/helpers/date-format';

import {
  formatRangeLabel,
  getRangesLabel,
} from '../../matrix-step/definition-tab.constants';

import {
  DEFAULT_AWARDING_LOGIC,
  DEFAULT_SPECIAL_AWARD_AMOUNT,
  ELIGIBILITY_CURRENCY_CRITERIA,
  SPECIAL_AWARD_PRESETS,
} from '../calculations-step.constants';
import { getCeebUnmatchedMessage, splitStoredCeebCodes } from './ceeb-codes';
import type {
  SpecialAwardLogicCategory,
  SpecialAwardRegime,
} from '../hooks/use-special-award-form';

import {
  EligibilityCriteriaDtoCriteria,
  EligibilityCriteriaDtoOperand,
} from 'api/generated/schemas';
import type {
  CalculationRuleDto,
  CalculationRuleRequest,
  EligibilityCriteriaDto,
} from 'api/generated/schemas';

/** Matched/total counts for one CEEB `values` csv, keyed by that exact string. */
export type CeebMatchSummaries = Record<
  string,
  { matched: number; total: number }
>;

/**
 * Maps a persisted rule back to its request body so inline edits (amount /
 * logic) can be PUT without dropping the rule's presets, description or
 * population source. `awardAmount` / `awardLogic` are required by the backend;
 * the defaults here only apply to a legacy rule that predates that validation.
 */
export const ruleToRequest = (
  rule: CalculationRuleDto
): CalculationRuleRequest => ({
  description: rule.description,
  campusVisit: rule.campusVisit,
  pellGrant: rule.pellGrant,
  fafsaGrant: rule.fafsaGrant,
  customLogicIds: rule.customLogicIds,
  regimeCalculationIds: rule.regimeCalculationIds,
  eligibilityCriterias: rule.eligibilityCriterias?.map(
    ({ id: _id, ...criteria }) => criteria
  ),
  awardAmount: rule.awardAmount ?? DEFAULT_SPECIAL_AWARD_AMOUNT,
  awardLogic: rule.awardLogic ?? DEFAULT_AWARDING_LOGIC,
});

/** Preset labels (Campus visitors / Pell Grant recipients / FAFSA filers) active on a rule. */
export const getRulePresetLabels = (rule: CalculationRuleDto): string[] =>
  SPECIAL_AWARD_PRESETS.filter((preset) => rule[preset.key]).map(
    (preset) => preset.label
  );

export interface SpecialAwardChipContext {
  regimes: SpecialAwardRegime[];
  categories: SpecialAwardLogicCategory[];
  /** Dictionary labels for the eligibility dimension, keyed by enum name. */
  criteriaLabels: Record<string, string>;
  /** Dictionary labels for the eligibility condition, keyed by enum name. */
  operandLabels: Record<string, string>;
  /** Academic rank label ("1", "2", …) keyed by academic rank id. */
  rankLabels: Record<string, string>;
  /** Live matched/total counts per CEEB `values` csv (FR-29/FR-30). */
  ceebMatchSummaries: CeebMatchSummaries;
}

const formatEligibilityAmount = (
  criteria: EligibilityCriteriaDto['criteria'],
  amount: number | undefined
): string => {
  if (amount == null) {
    return '';
  }

  return ELIGIBILITY_CURRENCY_CRITERIA.has(criteria)
    ? `$${amount.toLocaleString('en-US')}`
    : String(amount);
};

const getStudentDataChips = (
  customLogicIds: number[],
  categories: SpecialAwardLogicCategory[]
): string[] => {
  const idSet = new Set(customLogicIds.map(String));
  const namesByCategory = new Map<string, string[]>();

  categories.forEach((category) => {
    category.options?.forEach((option) => {
      if (!idSet.has(String(option.id))) {
        return;
      }

      const current = namesByCategory.get(category.category) ?? [];

      current.push(option.name);
      namesByCategory.set(category.category, current);
    });
  });

  return [...namesByCategory.entries()].map(
    ([category, names]) => `${category}:${names.join(', ')}`
  );
};

const getRegimeChips = (
  regimeCalculationIds: number[],
  regimes: SpecialAwardRegime[],
  rankLabels: Record<string, string>
): string[] => {
  const idSet = new Set(regimeCalculationIds.map(String));
  const chips: string[] = [];
  const rangeChips = new Set<string>();
  const rankChips = new Set<string>();

  regimes.forEach((regime) => {
    let matched = false;

    (regime.calculation ?? []).forEach((cell) => {
      if (cell.id == null || !idSet.has(String(cell.id))) {
        return;
      }

      matched = true;

      if (cell.range) {
        const rangeLabel = formatRangeLabel({
          from: cell.range.from ?? null,
          to: cell.range.to ?? null,
        });

        if (rangeLabel) {
          rangeChips.add(
            `${
              getRangesLabel(regime.awardType ?? '') || 'Range'
            }: ${rangeLabel}`
          );
        }
      }

      if (cell.academicRankId != null) {
        rankChips.add(
          `Academic/Scholarship: ${
            rankLabels[String(cell.academicRankId)] ?? cell.academicRankId
          }`
        );
      }
    });

    if (matched) {
      chips.push(regime.name ?? `Regime ${regime.id}`);
    }
  });

  return [...chips, ...rangeChips, ...rankChips];
};

/**
 * FR-29: N is the matched-code count, not the total entered. While the live
 * re-validation query hasn't resolved yet, optimistically shows the total so
 * the chip doesn't flash "0 CEEB codes" on first render.
 */
const getCeebChipCount = (
  values: string | undefined,
  ceebMatchSummaries: CeebMatchSummaries
): number => {
  const total = splitStoredCeebCodes(values).length;

  return ceebMatchSummaries[values ?? '']?.matched ?? total;
};

const getEligibilityChip = (
  criteria: EligibilityCriteriaDto,
  criteriaLabels: Record<string, string>,
  operandLabels: Record<string, string>,
  ceebMatchSummaries: CeebMatchSummaries
): string => {
  const dimension = criteriaLabels[criteria.criteria] ?? criteria.criteria;

  // Distance and App Date always persist as BETWEEN server-side (there is no
  // user-selectable condition for either), so they need their own chip shape
  // rather than falling into the generic BETWEEN branch below.
  if (criteria.criteria === EligibilityCriteriaDtoCriteria.DISTANCE) {
    return `Distance: ${criteria.amountFrom} - ${criteria.amountTo} miles`;
  }

  if (criteria.criteria === EligibilityCriteriaDtoCriteria.APP_DATE) {
    return `App date: ${formatIsoToDisplay(
      criteria.dateFrom
    )} – ${formatIsoToDisplay(criteria.dateTo)}`;
  }

  if (criteria.operand === EligibilityCriteriaDtoOperand.IN) {
    return `${getCeebChipCount(
      criteria.values,
      ceebMatchSummaries
    )} CEEB codes`;
  }

  const condition = criteria.operand
    ? operandLabels[criteria.operand] ?? criteria.operand
    : '';

  if (criteria.operand === EligibilityCriteriaDtoOperand.BETWEEN) {
    const from = formatEligibilityAmount(
      criteria.criteria,
      criteria.amountFrom
    );
    const to = formatEligibilityAmount(criteria.criteria, criteria.amountTo);

    return `${dimension} ${condition} ${from} and ${to}`;
  }

  return `${dimension} ${condition} ${formatEligibilityAmount(
    criteria.criteria,
    criteria.amount
  )}`;
};

/**
 * The chips shown in a row's DETAILS cell: one per predefined population, per
 * student-data dimension, per regime/range/rank narrowing, and per
 * eligibility criterion — all combined, since a rule can carry more than one
 * source at once.
 */
export const getSpecialAwardChips = (
  rule: CalculationRuleDto,
  {
    regimes,
    categories,
    criteriaLabels,
    operandLabels,
    rankLabels,
    ceebMatchSummaries,
  }: SpecialAwardChipContext
): string[] => [
  ...getRulePresetLabels(rule),
  ...(rule.customLogicIds?.length
    ? getStudentDataChips(rule.customLogicIds, categories)
    : []),
  ...(rule.regimeCalculationIds?.length
    ? getRegimeChips(rule.regimeCalculationIds, regimes, rankLabels)
    : []),
  ...(rule.eligibilityCriterias?.map((criteria) =>
    getEligibilityChip(
      criteria,
      criteriaLabels,
      operandLabels,
      ceebMatchSummaries
    )
  ) ?? []),
];

/**
 * FR-30: the same unmatched-count warning shown in the modal (FR-23/FR-24),
 * rendered directly in the row so the state is visible without reopening
 * anything. `null` when the rule has no CEEB criterion or nothing is unmatched.
 */
export const getCeebRowWarning = (
  rule: CalculationRuleDto,
  ceebMatchSummaries: CeebMatchSummaries
): string | null => {
  const ceebCriteria = rule.eligibilityCriterias?.find(
    (criteria) => criteria.operand === EligibilityCriteriaDtoOperand.IN
  );

  if (!ceebCriteria) {
    return null;
  }

  const total = splitStoredCeebCodes(ceebCriteria.values).length;
  const summary = ceebMatchSummaries[ceebCriteria.values ?? ''];

  if (!summary) {
    return null;
  }

  return getCeebUnmatchedMessage(summary.matched, total);
};
